import React, { useState, useEffect } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx, OWNER_LABEL } from "./Primitives";
import type { Partner, Deal, Contact, Org, MinutesDraft, Market, Principal } from "../types";

interface Props {
  open: boolean;
  prefill: { dealId?: string; contactId?: string; orgId?: string } | null;
  close: () => void;
  go: (screen: string, id?: string) => void;
  bump: () => void;
  partners: Record<string, Partner>;
}

type MinKind = "call" | "meeting" | "note" | "task";

interface TodoDraft {
  key: number;
  task: string;
  owner: string;
  due: string | null;
  keep: boolean;
}

interface ClientDraft {
  name: string;
  org: string;
  title: string;
  market: string;
  side: string;
  keep: boolean;
}

const KIND_META: Record<MinKind, { label: string; icon: (p: Record<string, unknown>) => React.ReactNode; sub: string }> = {
  call: { label: "Call", icon: I.phone, sub: "Paste call notes — Saga AI drafts a summary and to-dos" },
  meeting: { label: "Meeting", icon: I.meeting, sub: "Paste meeting notes — Saga AI drafts formal minutes you can download" },
  note: { label: "Note", icon: I.note, sub: "Write a quick note — Saga AI cleans it up and suggests follow-ups" },
  task: { label: "Task", icon: I.tasks, sub: "Quick-add a task with project, owner, and due date" },
};

function downloadAsWord(title: string, html: string) {
  const blob = new Blob(
    [`<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${title}</title></head><body>${html}</body></html>`],
    { type: "application/msword" }
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, "").trim()}.doc`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MinutesPanel({ open, prefill, close, go, bump, partners }: Props) {
  const [kind, setKind] = useState<MinKind>("call");
  const [text, setText] = useState("");
  const [dealId, setDealId] = useState("");
  const [contactId, setContactId] = useState("");
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ title: string; date: string; attendees: string[]; summary: string; points: string[] } | null>(null);
  const [todos, setTodos] = useState<TodoDraft[]>([]);
  const [client, setClient] = useState<ClientDraft | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [taskText, setTaskText] = useState("");
  const [taskOwner, setTaskOwner] = useState("geir");
  const [taskDue, setTaskDue] = useState("");
  const [taskNote, setTaskNote] = useState("");
  const [taskDealId, setTaskDealId] = useState("");
  const [taskSaved, setTaskSaved] = useState(false);

  const { data: deals, refetch: refetchDeals } = useData<Deal[]>("/api/deals");
  const { data: contacts, refetch: refetchContacts } = useData<Contact[]>("/api/contacts");
  const { data: orgs, refetch: refetchOrgs } = useData<Org[]>("/api/orgs");
  const { data: markets } = useData<Market[]>("/api/markets");
  const { data: principals } = useData<Principal[]>("/api/principals");

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, close]);

  const addOrg = (id: string | null | undefined) => {
    if (id) setOrgIds(ids => (ids.includes(id) ? ids : [...ids, id]));
  };
  // Picking a project or contact pulls their company into the meeting.
  const pickDeal = (id: string) => {
    setDealId(id);
    addOrg(deals?.find(d => d.id === id)?.operator_id);
  };
  const pickContact = (id: string) => {
    setContactId(id);
    addOrg(contacts?.find(c => c.id === id)?.org_id);
  };

  useEffect(() => {
    if (open) {
      setText("");
      setDraft(null);
      setTodos([]);
      setClient(null);
      setSaved(false);
      setSaving(false);
      setError(null);
      setDealId(prefill?.dealId || "");
      setContactId(prefill?.contactId || "");
      setOrgIds([]);
      setTaskText("");
      setTaskOwner("geir");
      setTaskDue("");
      setTaskNote("");
      setTaskDealId(prefill?.dealId || "");
      setTaskSaved(false);
    }
  }, [open, prefill]);

  // When opened from an account/project, start with its company selected.
  const prefillApplied = React.useRef(false);
  useEffect(() => {
    if (!open) return;
    prefillApplied.current = false;
    // Lists may have changed since the app loaded (new deals, edited accounts).
    refetchDeals(); refetchContacts(); refetchOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);
  useEffect(() => {
    if (!open || prefillApplied.current || !deals || !contacts) return;
    prefillApplied.current = true;
    const ids = [
      prefill?.orgId,
      deals.find(d => d.id === prefill?.dealId)?.operator_id,
      contacts.find(c => c.id === prefill?.contactId)?.org_id,
    ].filter((x): x is string => !!x);
    if (ids.length) setOrgIds([...new Set(ids)]);
  }, [open, prefill, deals, contacts]);


  const generate = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    setDraft(null);
    setTodos([]);
    setClient(null);
    try {
      const result = await api.post<MinutesDraft>("/api/ai/minutes", {
        text: text.trim(),
        dealId: dealId || null,
        contactId: contactId || null,
        orgIds,
        kind,
      });
      setDraft({
        title: result.title || "Meeting minutes",
        date: result.date || new Date().toISOString().slice(0, 10),
        attendees: result.attendees || [],
        summary: result.summary || "",
        points: result.points || [],
      });
      setTodos(
        (result.todos || []).map((t, i) => ({
          key: i, task: t.task || "", owner: t.owner || "trond", due: t.due || null, keep: true,
        }))
      );
      if (!contactId && result.client?.name) {
        setClient({ ...result.client, keep: true });
      }
    } catch {
      setError("Saga AI couldn't draft from that — try adding a little more detail.");
    }
    setBusy(false);
  };

  const setPoint = (i: number, v: string) => {
    setDraft(d => d ? { ...d, points: d.points.map((p, j) => j === i ? v : p) } : d);
  };
  const delPoint = (i: number) => {
    setDraft(d => d ? { ...d, points: d.points.filter((_, j) => j !== i) } : d);
  };
  const setTodo = (key: number, patch: Partial<TodoDraft>) => {
    setTodos(ts => ts.map(t => t.key === key ? { ...t, ...patch } : t));
  };

  const save = async () => {
    if (!draft || saving || saved) return;
    setSaving(true);
    try {
      let cid = contactId;
      if (!cid && client?.keep && client.name.trim()) {
        const newContact = await api.post<Contact>("/api/contacts", {
          name: client.name.trim(),
          org_name: client.org || "External",
          title: client.title || "Contact",
          side: client.side || "operator",
          market: client.market || "",
        });
        cid = newContact.id;
      }

      const saved = await api.post<{ id: string }>("/api/minutes", {
        deal_id: dealId || null,
        contact_id: cid || null,
        org_ids: orgIds,
        kind,
        title: draft.title,
        date: draft.date,
        attendees: draft.attendees,
        summary: draft.summary,
        points: draft.points,
      });

      const keptTodos = todos.filter(t => t.keep && t.task.trim());
      for (const t of keptTodos) {
        await api.post("/api/todos", {
          theme: draft.title,
          task: t.task.trim(),
          owner: t.owner,
          due: t.due || null,
          priority: "med",
          note: `From ${kind}: ${draft.title}`,
          deal_id: dealId || null,
        });
      }

      bump();
      setSaved(true);
      const goId = cid && cid !== contactId ? cid : null;
      setTimeout(() => {
        close();
        if (goId) go("clients", goId);
      }, 950);
    } catch {
      setError("Failed to save.");
      setSaving(false);
    }
  };

  const saveTask = async () => {
    if (!taskText.trim() || taskSaved) return;
    try {
      await api.post("/api/todos", {
        theme: "Quick task",
        task: taskText.trim(),
        owner: taskOwner,
        due: taskDue || null,
        priority: "med",
        note: taskNote || null,
        deal_id: taskDealId || null,
      });
      bump();
      setTaskSaved(true);
      setTimeout(() => close(), 800);
    } catch {
      setError("Failed to save task.");
    }
  };

  const downloadDoc = () => {
    if (!draft) return;
    const kindLabel = kind === "meeting" ? "Meeting Minutes" : kind === "note" ? "Note" : "Call Summary";
    const pts = draft.points.map(p => `<li style="margin-bottom:8px;font-size:13.5px;line-height:1.65;">${p}</li>`).join("\n");
    const html = `
      <div style="border-bottom:2px solid #c8502d;padding-bottom:14px;margin-bottom:24px;display:flex;align-items:center;justify-content:space-between;">
        <img src="/logo-saga.png" style="height:38px;" alt="Saga Advisory">
        <span style="font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#c8502d;font-weight:600;">${kindLabel}</span>
      </div>
      <h1 style="font-family:Georgia,serif;font-size:22px;margin:0 0 8px;color:#1a1a1a;">${draft.title}</h1>
      <div style="font-size:12px;color:#666;margin-bottom:24px;">
        <span style="margin-right:18px;">${draft.date}</span>
        ${draft.attendees.length ? `<span>Attendees: ${draft.attendees.join(", ")}</span>` : ""}
      </div>
      <div style="font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#c8502d;font-weight:700;margin:24px 0 8px;">Summary</div>
      <p style="font-size:14px;line-height:1.7;margin-bottom:20px;">${draft.summary}</p>
      ${pts ? `<div style="font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#c8502d;font-weight:700;margin:24px 0 8px;">Key Points</div><ul style="padding-left:20px;">${pts}</ul>` : ""}
      <div style="margin-top:40px;padding-top:14px;border-top:1px solid #ddd;font-size:10.5px;color:#999;">
        <strong style="color:#c8502d;">Saga Advisory</strong> — Confidential
      </div>`;
    downloadAsWord(draft.title, html);
  };

  const downloadPdf = () => {
    if (!draft) return;
    const kindLabel = kind === "meeting" ? "Meeting Minutes" : kind === "note" ? "Note" : "Call Summary";
    const pts = draft.points.map(p => `<li>${p}</li>`).join("\n");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${draft.title}</title>
<style>
@page{margin:2.5cm 2cm;size:A4}
body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;line-height:1.65;max-width:700px;margin:0 auto;padding:40px 20px}
.hdr{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #c8502d;padding-bottom:16px;margin-bottom:28px}
.logo{height:42px}
.dt{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#c8502d;font-weight:600}
h1{font-size:22px;margin:0 0 8px;font-weight:600}
.meta{font-size:12px;color:#666;margin-bottom:24px}
.meta span{margin-right:18px}
.sl{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#c8502d;font-weight:700;margin:24px 0 8px}
.sum{font-size:14px;line-height:1.7;margin-bottom:20px}
ul{padding-left:20px}
li{font-size:13.5px;line-height:1.65;margin-bottom:8px}
.ft{margin-top:40px;padding-top:14px;border-top:1px solid #ddd;font-size:10.5px;color:#999}
.ft strong{color:#c8502d}
</style></head><body>
<div class="hdr"><img src="/logo-saga.png" class="logo" alt="Saga Advisory"><span class="dt">${kindLabel}</span></div>
<h1>${draft.title}</h1>
<div class="meta"><span>${draft.date}</span>${draft.attendees.length ? `<span>Attendees: ${draft.attendees.join(", ")}</span>` : ""}</div>
<div class="sl">Summary</div><div class="sum">${draft.summary}</div>
${pts ? `<div class="sl">Key Points</div><ul>${pts}</ul>` : ""}
<div class="ft"><strong>Saga Advisory</strong> — Confidential</div>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 300);
    }
  };

  if (!open) return null;
  const keepN = todos.filter(t => t.keep && t.task.trim()).length;
  const newClient = client?.keep && client.name.trim();
  const meta = KIND_META[kind];

  return (
    <div className="ai-overlay" onMouseDown={close}>
      <div className="min-panel" onMouseDown={e => e.stopPropagation()}>
        <div className="ai-head">
          <span className="ai-spark">{meta.icon({ size: 16, w: 1.9 })}</span>
          <div>
            <div className="ai-title">{meta.label}</div>
            <div className="ai-sub">{meta.sub}</div>
          </div>
          <span className="cmd-esc">esc</span>
        </div>

        <div className="min-kind-bar">
          {(Object.keys(KIND_META) as MinKind[]).map(k => (
            <button key={k} className={clsx("min-kind-btn", kind === k && "active")}
              onClick={() => { setKind(k); setDraft(null); setTodos([]); setClient(null); setSaved(false); setTaskSaved(false); setError(null); }}>
              {KIND_META[k].icon({ size: 13, w: 1.8 })}
              {KIND_META[k].label}
            </button>
          ))}
        </div>

        <div className="min-scroll">
          {kind === "task" ? (
            <div className="min-inputs">
              <div className="min-quick-task">
                <input className="min-task-main" placeholder="What needs to be done?"
                  value={taskText} onChange={e => setTaskText(e.target.value)}
                  autoFocus />
                <textarea className="min-task-note" rows={2} placeholder="Short note (optional)"
                  value={taskNote} onChange={e => setTaskNote(e.target.value)} />
                <div className="min-selects">
                  <label className="min-field">
                    <span>Project</span>
                    <select value={taskDealId} onChange={e => setTaskDealId(e.target.value)} className="min-select">
                      <option value="">— none —</option>
                      {deals?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </label>
                  <label className="min-field">
                    <span>Owner</span>
                    <select value={taskOwner} onChange={e => setTaskOwner(e.target.value)} className="min-select">
                      {["geir", "trond", "bjorn"].map(o =>
                        <option key={o} value={o}>{OWNER_LABEL[o]}</option>
                      )}
                    </select>
                  </label>
                  <label className="min-field">
                    <span>Due</span>
                    <input type="date" className="min-select" value={taskDue}
                      onChange={e => setTaskDue(e.target.value)} />
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="min-inputs">
                <div className="min-selects">
                  <ProjectPicker value={dealId} deals={deals || []} markets={markets || []} principals={principals || []}
                    onPick={pickDeal}
                    onCreated={(d) => { refetchDeals(); setDealId(d.id); }} />
                  <ContactPicker value={contactId} contacts={contacts || []} orgs={orgs || []}
                    onPick={pickContact}
                    onCreated={(c) => { refetchContacts(); refetchOrgs(); setContactId(c.id); addOrg(c.org_id); }} />
                </div>
                <CompanyPicker selected={orgIds} orgs={orgs || []} onChange={setOrgIds}
                  onCreated={(o) => { refetchOrgs(); addOrg(o.id); }} />
                <textarea className="min-transcript" rows={kind === "meeting" ? 8 : 5}
                  placeholder={kind === "meeting"
                    ? "Paste the meeting transcript or your detailed notes here…"
                    : kind === "note"
                    ? "Write your note here…"
                    : "Paste the call transcript or your rough notes here…"}
                  value={text} onChange={e => setText(e.target.value)} />
                <div className="min-input-foot">
                  {kind === "call" && (
                    <button className="min-sample" onClick={() => setText("Call with Marc at Perenco, ~20 min. He confirmed the Congo brownfield infill is being scoped right now and timing matters. Wants a one-page Fishbones summary and a named local-content partner before he routes us internally. Bjørn to pull the candidate well list. I'll send the one-pager this week. Marc will introduce us to the asset team once he has it. We agreed to reconnect next Thursday.")}>
                      Use a sample
                    </button>
                  )}
                  {kind !== "call" && <span />}
                  <button className="btn primary sm" onClick={generate} disabled={busy || !text.trim()}>
                    {I.ai({ size: 14 })} {busy ? "Drafting…" : draft ? "Regenerate" : kind === "note" ? "Clean up with AI" : "Generate " + meta.label.toLowerCase()}
                  </button>
                </div>
              </div>

              {error && <div className="ai-error">{error}</div>}

              {draft && (
                <div className="min-draft">
                  <div className="min-draft-head">
                    <div className="min-draft-label">Draft — edit anything</div>
                    {kind !== "note" && (
                      <div className="min-download-btns">
                        <button className="btn ghost sm" onClick={downloadPdf} title="Print / save as PDF">
                          {I.doc({ size: 13, w: 1.8 })} PDF
                        </button>
                        <button className="btn ghost sm" onClick={downloadDoc} title="Download as Word">
                          {I.doc({ size: 13, w: 1.8 })} Word
                        </button>
                      </div>
                    )}
                  </div>
                  <input className="min-title-input" value={draft.title}
                    onChange={e => setDraft(d => d ? { ...d, title: e.target.value } : d)} />
                  <div className="min-att-line">
                    {draft.attendees.length > 0 && <>Attendees: {draft.attendees.join(", ")} &middot; </>}
                    <span className="mono">{draft.date}</span>
                  </div>
                  <textarea className="min-summary-input" rows={kind === "meeting" ? 5 : 3} value={draft.summary}
                    onChange={e => setDraft(d => d ? { ...d, summary: e.target.value } : d)} />

                  {draft.points.length > 0 && (
                    <>
                      <div className="min-points-label">Key points</div>
                      <div className="min-points-edit">
                        {draft.points.map((p, i) => (
                          <div key={i} className="mpe-row">
                            <span className="mpe-bullet" />
                            <input className="mpe-input" value={p} onChange={e => setPoint(i, e.target.value)} />
                            <button className="mpe-del" onClick={() => delPoint(i)}>
                              {I.plus({ size: 14, style: { transform: "rotate(45deg)" } })}
                            </button>
                          </div>
                        ))}
                        <button className="mpe-add" onClick={() => setDraft(d => d ? { ...d, points: [...d.points, ""] } : d)}>
                          {I.plus({ size: 13 })} Add point
                        </button>
                      </div>
                    </>
                  )}

                  {client && (
                    <div className={clsx("min-client", !client.keep && "dropped")}>
                      <div className="min-client-head">
                        <button className={clsx("checkbox", client.keep && "checked")}
                          onClick={() => setClient(c => c ? { ...c, keep: !c.keep } : c)}>
                          {client.keep && I.check({ size: 13, w: 2.4 })}
                        </button>
                        <span className="min-client-label">{I.meeting({ size: 13, w: 1.8 })} New client profile</span>
                        <span className="min-client-hint">not in your contacts yet</span>
                      </div>
                      {client.keep && (
                        <div className="min-client-fields">
                          <input className="mcf-input" placeholder="Name" value={client.name}
                            onChange={e => setClient(c => c ? { ...c, name: e.target.value } : c)} />
                          <input className="mcf-input" placeholder="Organisation" value={client.org}
                            onChange={e => setClient(c => c ? { ...c, org: e.target.value } : c)} />
                          <input className="mcf-input" placeholder="Title" value={client.title}
                            onChange={e => setClient(c => c ? { ...c, title: e.target.value } : c)} />
                          <input className="mcf-input sm" placeholder="Market" value={client.market}
                            onChange={e => setClient(c => c ? { ...c, market: e.target.value } : c)} />
                          <select className="mcf-select" value={client.side}
                            onChange={e => setClient(c => c ? { ...c, side: e.target.value } : c)}>
                            {["operator", "noc", "government", "principal"].map(s =>
                              <option key={s} value={s}>{s}</option>
                            )}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="min-todos">
                    <div className="min-todos-label">
                      {I.ai({ size: 13 })} Suggested to-dos
                      <span className="min-todos-n">{keepN} of {todos.length} kept</span>
                    </div>
                    {todos.length === 0 ? (
                      <div className="min-todos-empty">No to-dos suggested.</div>
                    ) : (
                      todos.map(t => (
                        <div key={t.key} className={clsx("min-todo", !t.keep && "dropped")}>
                          <button className={clsx("checkbox", t.keep && "checked")}
                            onClick={() => setTodo(t.key, { keep: !t.keep })}>
                            {t.keep && I.check({ size: 13, w: 2.4 })}
                          </button>
                          <input className="min-todo-input" value={t.task}
                            onChange={e => setTodo(t.key, { task: e.target.value })} />
                          <select className="min-todo-owner" value={t.owner}
                            onChange={e => setTodo(t.key, { owner: e.target.value })}>
                            {["geir", "trond", "bjorn"].map(o =>
                              <option key={o} value={o}>{OWNER_LABEL[o]}</option>
                            )}
                          </select>
                          <input type="date" className="min-todo-due" value={t.due || ""}
                            onChange={e => setTodo(t.key, { due: e.target.value || null })} />
                          <button className="mpe-del" onClick={() => setTodos(ts => ts.filter(x => x.key !== t.key))}>
                            {I.plus({ size: 14, style: { transform: "rotate(45deg)" } })}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {kind === "task" ? (
          <div className="min-foot">
            {taskSaved ? (
              <span className="compose-sent">
                {I.check({ size: 15, w: 2.4 })} Task added
              </span>
            ) : (
              <>
                <span className="min-foot-note">
                  {taskText.trim() ? `Add task for ${OWNER_LABEL[taskOwner] || taskOwner}` : "Type a task above"}
                </span>
                <div className="min-foot-btns">
                  <button className="btn ghost sm" onClick={close}>Cancel</button>
                  <button className="btn primary sm" onClick={saveTask} disabled={!taskText.trim()}>
                    {I.plus({ size: 14 })} Add task
                  </button>
                </div>
              </>
            )}
          </div>
        ) : draft ? (
          <div className="min-foot">
            {saved ? (
              <span className="compose-sent">
                {I.check({ size: 15, w: 2.4 })}
                Saved{newClient ? " + client" : ""} + {keepN} to-do{keepN === 1 ? "" : "s"}
              </span>
            ) : (
              <>
                <span className="min-foot-note">
                  Saves {kind}{newClient ? `, creates client "${client!.name.trim()}"` : ""}
                  {keepN ? ` and adds ${keepN} to-do${keepN === 1 ? "" : "s"}` : ""}
                </span>
                <div className="min-foot-btns">
                  <button className="btn ghost sm" onClick={close}>Cancel</button>
                  <button className="btn primary sm" onClick={save} disabled={saving || saved}>
                    {I.check({ size: 14, w: 2.4 })} {saving ? "Saving…" : "Approve & save"}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}


/* ─── Pickers: choose an existing record or create one on the spot ─── */

const NEW = "__new";

function ProjectPicker({ value, deals, markets, principals, onPick, onCreated }: {
  value: string; deals: Deal[]; markets: Market[]; principals: Principal[];
  onPick: (id: string) => void; onCreated: (d: Deal) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [market, setMarket] = useState("");
  const [principal, setPrincipal] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (busy) return;
    setErr(null); setBusy(true);
    try {
      const d = await api.post<Deal>("/api/deals", { name: name.trim(), market: market || null });
      if (principal) await api.put(`/api/deals/${d.id}`, { principal_id: principal });
      onCreated(d);
      setAdding(false); setName(""); setMarket(""); setPrincipal("");
    } catch (e) { setErr(e instanceof Error ? e.message : "Couldn't create the project"); }
    setBusy(false);
  };
  return (
    <label className="min-field">
      <span>Project</span>
      {adding ? (
        <div className="min-new">
          <input className="min-select" autoFocus placeholder="Project name" value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && name.trim() && create()} />
          <div className="min-new-row">
            <select className="min-select" value={market} onChange={e => setMarket(e.target.value)}>
              <option value="">Market…</option>
              {markets.map(m => <option key={m.slug} value={m.name}>{m.name}</option>)}
            </select>
            <select className="min-select" value={principal} onChange={e => setPrincipal(e.target.value)}>
              <option value="">Principal…</option>
              {principals.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {err && <div className="min-new-err">{err}</div>}
          <div className="min-new-row">
            <button type="button" className="btn ghost sm" onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="btn primary sm" disabled={!name.trim() || busy} onClick={create}>{busy ? "Adding…" : "Add project"}</button>
          </div>
        </div>
      ) : (
        <select value={value} className="min-select" onChange={e => e.target.value === NEW ? setAdding(true) : onPick(e.target.value)}>
          <option value="">— none —</option>
          {deals.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          <option value={NEW}>+ New project…</option>
        </select>
      )}
    </label>
  );
}

function ContactPicker({ value, contacts, orgs, onPick, onCreated }: {
  value: string; contacts: Contact[]; orgs: Org[];
  onPick: (id: string) => void; onCreated: (c: Contact) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", title: "", email: "", org: "", newOrg: "" });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (busy) return;
    setErr(null); setBusy(true);
    try {
      let org: Org | undefined = orgs.find(o => o.id === f.org);
      if (f.org === NEW && f.newOrg.trim()) org = await api.post<Org>("/api/orgs", { name: f.newOrg.trim() });
      const c = await api.post<Contact>("/api/contacts", {
        name: f.name.trim(), title: f.title.trim() || null, email: f.email.trim() || null,
        org_id: org?.id || null, org_name: org?.name || null,
        side: org?.type ? org.type.toLowerCase() : null,
      });
      onCreated(c);
      setAdding(false); setF({ name: "", title: "", email: "", org: "", newOrg: "" });
    } catch (e) { setErr(e instanceof Error ? e.message : "Couldn't create the contact"); }
    setBusy(false);
  };
  return (
    <label className="min-field">
      <span>Contact</span>
      {adding ? (
        <div className="min-new">
          <input className="min-select" autoFocus placeholder="Full name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
          <div className="min-new-row">
            <input className="min-select" placeholder="Title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} />
            <input className="min-select" placeholder="Email" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          </div>
          {f.org === NEW ? (
            <input className="min-select" placeholder="New company name" value={f.newOrg} onChange={e => setF({ ...f, newOrg: e.target.value })} />
          ) : (
            <select className="min-select" value={f.org} onChange={e => setF({ ...f, org: e.target.value })}>
              <option value="">Company…</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              <option value={NEW}>+ New company…</option>
            </select>
          )}
          {err && <div className="min-new-err">{err}</div>}
          <div className="min-new-row">
            <button type="button" className="btn ghost sm" onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="btn primary sm" disabled={!f.name.trim() || busy} onClick={create}>{busy ? "Adding…" : "Add contact"}</button>
          </div>
        </div>
      ) : (
        <select value={value} className="min-select" onChange={e => e.target.value === NEW ? setAdding(true) : onPick(e.target.value)}>
          <option value="">— none —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.org_name ? ` · ${c.org_name}` : ""}</option>)}
          <option value={NEW}>+ New contact…</option>
        </select>
      )}
    </label>
  );
}

function CompanyPicker({ selected, orgs, onChange, onCreated }: {
  selected: string[]; orgs: Org[]; onChange: (ids: string[]) => void; onCreated: (o: Org) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    const o = await api.post<Org>("/api/orgs", { name: name.trim() });
    onCreated(o);
    setAdding(false); setName("");
  };
  const byId = new Map(orgs.map(o => [o.id, o]));
  return (
    <div className="min-field min-companies">
      <span>Companies in this meeting</span>
      <div className="min-chips">
        {selected.map(id => (
          <span key={id} className="set-chip">
            {byId.get(id)?.name || id}
            <button type="button" aria-label="Remove company" onClick={() => onChange(selected.filter(x => x !== id))}>×</button>
          </span>
        ))}
        {adding ? (
          <span className="min-new-row">
            <input className="min-select" autoFocus placeholder="Company name" value={name}
              onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && create()} />
            <button type="button" className="btn primary sm" disabled={!name.trim()} onClick={create}>Add</button>
            <button type="button" className="btn ghost sm" onClick={() => setAdding(false)}>Cancel</button>
          </span>
        ) : (
          <select className="min-select min-add-co" value="" onChange={e => {
            if (e.target.value === NEW) setAdding(true);
            else if (e.target.value) onChange([...selected, e.target.value]);
          }}>
            <option value="">{selected.length ? "+ Add another company" : "+ Add company"}</option>
            {orgs.filter(o => !selected.includes(o.id)).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            <option value={NEW}>+ New company…</option>
          </select>
        )}
      </div>
    </div>
  );
}
